import { Component, effect, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, Validators } from '@angular/forms';
import { IdName } from 'src/app/shared/models/id-name';
import { JobPositionState } from 'src/app/competence/state/job-position.state';
import { Subscription } from 'rxjs';
import { select, Store } from '@ngxs/store';
import { LoadAllJobPositionsForDepartment } from 'src/app/competence/state/job-position.actions';
import { datepickerValueToDate } from 'src/app/shared/functions/datepicker-value-to-date';
import {
  addMinutes,
  areIntervalsOverlapping,
  endOfDay,
  isSameDay,
  isWithinInterval,
  startOfDay,
  subMinutes,
} from 'date-fns';
import { ICrewMemberUpdate, IPaxUpdate } from '../../models/trip.model';
import { UpdateCrewMember, UpdatePax } from '../../state/trips/trip.actions';
import { IPlannedActivityFromBasicTrip } from '../../models/planned-activities/planned-activity.model';
import { ITripPax } from '../../models/pax-overview/pax-overview.model';
import { TripStatus } from '../../enums/trip-status.enum';

type CrewMemberPosition = {
  id: string | null;
  position: FormControl<string | null>;
  from: FormControl<Date | null>;
  to: FormControl<Date | null>;
  minTo: Date | null;
  maxFrom: Date | null;
  comment: FormControl<string | null>;
  error?: string;
  conflictsWith?: CrewMemberPosition[];
};
@Component({
  selector: 'app-edit-crew-member-dates-modal',
  templateUrl: './edit-crew-member-dates-modal.component.html',
  styleUrls: ['./edit-crew-member-dates-modal.component.scss'],
  standalone: false,
})
export class EditCrewMemberDatesModalComponent implements OnDestroy {
  fromControl = new FormControl(new Date(this.data.from), [Validators.required]);
  toControl = new FormControl(new Date(this.data.to), [Validators.required]);
  positionOnTripCtrl = new FormControl(this.data.position.id, [Validators.required]);
  jobPositionsForDepartment = select(JobPositionState.getJobPositionsForDepartment);
  positions: CrewMemberPosition[] = [];
  isFormValid = true;
  positionsNames: any = {};
  from = startOfDay(new Date(this.data.tripFrom));
  to = endOfDay(new Date(this.data.tripTo));
  ddd = datepickerValueToDate;
  userCardData: any;

  private readonly positionDateChangeWarnings = new Set<CrewMemberPosition>();
  private readonly dateBaselineByPosition = new WeakMap<
    CrewMemberPosition,
    { from: Date | null; to: Date | null }
  >();
  private subscription = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<EditCrewMemberDatesModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: IPlannedActivityFromBasicTrip &
      ITripPax & {
        tripId: string;
        splitActivities: (IPlannedActivityFromBasicTrip & ITripPax)[];
        tripFrom: Date;
        tripTo: Date;
        tripStatus?: TripStatus;
        isPax?: boolean;
      },
    private store: Store
  ) {
    this.positions.push(
      this.createPositionControls(
        this.data.isPax ? this.data.paxOverviewId : this.data.plannedActivityId,
        this.data.position.id,
        new Date(this.data.from),
        new Date(this.data.to),
        this.data.comment
      )
    );
    if (this.data.splitActivities?.length > 0) {
      this.data.splitActivities.forEach((activity) => {
        this.positions.push(
          this.createPositionControls(
            this.data.isPax ? activity.paxOverviewId : activity.plannedActivityId,
            activity.position.id,
            new Date(activity.from),
            new Date(activity.to),
            activity.comment
          )
        );
      });
    }
    if (!this.data.isPax) {
      this.store.dispatch(new LoadAllJobPositionsForDepartment());
    }

    effect(() => {
      const positions = this.jobPositionsForDepartment();
      positions?.forEach((position) => {
        this.positionsNames[position.id] = position.name;
      });
    });

    this.userCardData = {
      ...this.data,
      positionName: this.data.position?.name,
      profilePictureUrl: this.data.profilePictureUrl,
    };

    // Validate all positions after initialization to detect existing errors
    this.positions.forEach((position) => this.validatePositionDates(position));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  addPosition(): void {
    this.positions.push(this.createPositionControls());
    this.positions.forEach((position) => this.validatePositionDates(position));
  }

  shouldShowReopenedDocumentsWarning(position: CrewMemberPosition): boolean {
    return this.positionDateChangeWarnings.has(position);
  }

  deletePosition(index: number): void {
    this.positions.splice(index, 1);
    if (this.positions.length === 1) {
      this.removeControlError(this.positions[0].from, ['startOverlapping', 'endOverlapping']);
      this.removeControlError(this.positions[0].to, ['startOverlapping', 'endOverlapping']);
    }
    this.positions.forEach((position) => this.validatePositionDates(position));
  }

  areAllIntervalsValid(): boolean {
    return this.positions.every(
      ({ from, to }) => from.value && to.value && from.errors === null && to.errors === null
    );
  }

  validatePositionDates(position: CrewMemberPosition): void {
    if (!this.areAllIntervalsValid()) {
      this.isFormValid = false;
    }
    const range = this.positionToInterval(position);
    if (!this.isIntervalValid(range)) {
      this.isFormValid = false;
      return;
    }
    const conflicts = this.positions.filter((otherPosition) => {
      const otherRange = this.positionToInterval(otherPosition);
      if (position === otherPosition) {
        return false;
      }
      if (!this.isIntervalValid(otherRange) || !this.isIntervalValid(range)) {
        this.isFormValid = false;
        return false;
      }
      const isStartOverlapping = isWithinInterval(range.start, otherRange);
      const isEndOverlapping = isWithinInterval(range.end, otherRange);
      const isIntervalsOverlapping = areIntervalsOverlapping(range, otherRange);
      this.setErrorOnCondition(position.from, isStartOverlapping, 'startOverlapping');
      this.setErrorOnCondition(position.to, isEndOverlapping, 'endOverlapping');

      return isStartOverlapping || isEndOverlapping || isIntervalsOverlapping;
    });
    position.error = !conflicts.length ? undefined : 'dateRangeOverlappingWithOtherPositons';
    position.conflictsWith = !conflicts.length ? undefined : conflicts;
    this.isFormValid = this.positions.every((p) =>
      !this.data.isPax ? p.position.valid && !p.error : !p.error
    );
  }

  save(): void {
    if (this.data.isPax) {
      this.savePax();
    } else {
      this.saveCrewMember();
    }
  }

  saveCrewMember(): void {
    const crewMember: ICrewMemberUpdate = {
      onBoardData: this.positions.map((position) => ({
        id: position.id,
        positionId: `${position.position.value}`,
        from: datepickerValueToDate(position.from.value as Date).toISOString(),
        to: datepickerValueToDate(position.to.value as Date).toISOString(),
        comment: position.comment.value || '',
      })),
    };
    this.store.dispatch(new UpdateCrewMember(this.data.tripId, this.data.userId, crewMember));
  }

  savePax(): void {
    const pax: IPaxUpdate = {
      paxes: this.positions.map((position) => ({
        paxOverviewId: position.id,
        startDate: datepickerValueToDate(position.from.value as Date).toISOString(),
        endDate: datepickerValueToDate(position.to.value as Date).toISOString(),
      })),
    };
    this.store.dispatch(new UpdatePax(this.data.tripId, this.data.paxId, pax));
  }

  private createPositionControls(
    id: string | null = null,
    positionId: string | null = null,
    from: Date | null = null,
    to: Date | null = null,
    comment = ''
  ): CrewMemberPosition {
    const position: CrewMemberPosition = {
      id,
      position: new FormControl(positionId, [Validators.required]),
      from: new FormControl(from, [Validators.required]),
      to: new FormControl(to, [Validators.required]),
      minTo: from ? this.addMinute(from) : null,
      maxFrom: to ? this.subMinute(to) : null,
      comment: new FormControl(comment),
    };
    this.subscription.add(
      position.position.valueChanges.subscribe(() =>
        this.positions.forEach((p) => this.validatePositionDates(p))
      )
    );
    this.subscription.add(
      position.from.valueChanges.subscribe(() => {
        const newFrom = datepickerValueToDate(position.from.value as Date);
        position.minTo = this.addMinute(datepickerValueToDate(position.from.value as Date));
        position.to.updateValueAndValidity({ emitEvent: false });
        this.setErrorOnCondition(position.from, newFrom < this.from, 'tooEarly');
        this.setErrorOnCondition(position.from, newFrom > (position.maxFrom || this.to), 'tooLate');
        this.positions.forEach((p) => this.validatePositionDates(p));
        this.updatePositionDateChangeWarning(position);
      })
    );
    this.subscription.add(
      position.to.valueChanges.subscribe(() => {
        const newTo = datepickerValueToDate(position.to.value as Date);
        position.maxFrom = this.subMinute(datepickerValueToDate(position.to.value as Date));
        position.from.updateValueAndValidity({ emitEvent: false });
        this.setErrorOnCondition(position.to, newTo > this.to, 'tooLate');
        this.setErrorOnCondition(position.to, newTo < (position.minTo || this.from), 'tooEarly');
        this.positions.forEach((p) => this.validatePositionDates(p));
        this.updatePositionDateChangeWarning(position);
      })
    );
    if (this.data.isPax) {
      position.position.disable();
      position.comment.disable();
    }
    if (this.data.lastMonthSignedDocumentPeriodDate) {
      position.from.disable();
      position.to.disable();
    }
    this.capturePositionDateBaseline(position);
    return position;
  }

  private shouldWarnAboutReopenedDocuments(): boolean {
    return this.data.tripStatus === TripStatus.Completed && !!this.data.hasAssignedWorkSchedules;
  }

  private capturePositionDateBaseline(position: CrewMemberPosition): void {
    this.dateBaselineByPosition.set(position, {
      from: position.from.value ? datepickerValueToDate(position.from.value as Date) : null,
      to: position.to.value ? datepickerValueToDate(position.to.value as Date) : null,
    });
  }

  private updatePositionDateChangeWarning(position: CrewMemberPosition): void {
    if (!this.shouldWarnAboutReopenedDocuments()) {
      this.positionDateChangeWarnings.delete(position);
      return;
    }

    if (this.hasPositionDatesChangedFromBaseline(position)) {
      this.positionDateChangeWarnings.add(position);
      return;
    }

    this.positionDateChangeWarnings.delete(position);
  }

  private hasPositionDatesChangedFromBaseline(position: CrewMemberPosition): boolean {
    const baseline = this.dateBaselineByPosition.get(position);
    if (!baseline) {
      return false;
    }

    const fromChanged = !this.dateValuesEqualDay(position.from.value, baseline.from);
    const toChanged = !this.dateValuesEqualDay(position.to.value, baseline.to);
    return fromChanged || toChanged;
  }

  private dateValuesEqualDay(current: unknown, baseline: Date | null): boolean {
    const base = baseline ?? null;
    const currentEmpty = current === null || current === undefined || current === '';
    if (currentEmpty && !base) {
      return true;
    }
    if (currentEmpty || !base) {
      return false;
    }
    return isSameDay(datepickerValueToDate(current as Date), datepickerValueToDate(base));
  }

  private positionToInterval(position: CrewMemberPosition): Interval {
    return {
      start: datepickerValueToDate(position.from.value as Date),
      end: datepickerValueToDate(position.to.value as Date),
    };
  }

  private isIntervalValid(interval: Interval): boolean {
    return interval.start < interval.end;
  }

  private addMinute(date: Date): Date | null {
    return date ? addMinutes(date, 1) : null;
  }

  private subMinute(date: Date): Date | null {
    return date ? subMinutes(date, 1) : null;
  }

  private addControlError(control: FormControl, errorObj: Record<string, any>): void {
    const currentErrors = control.errors || {};
    const newErrors = { ...currentErrors, ...errorObj };
    control.setErrors(Object.keys(newErrors).length ? newErrors : null);
  }

  private removeControlError(control: FormControl, errorNames: string | string[]): void {
    const currentErrors = control.errors || {};
    if (!currentErrors || Object.keys(currentErrors).length === 0) {
      return;
    }

    const errorsToRemove = Array.isArray(errorNames) ? errorNames : [errorNames];
    const updatedErrors = { ...currentErrors };

    errorsToRemove.forEach((errorName) => {
      delete updatedErrors[errorName];
    });

    control.setErrors(Object.keys(updatedErrors).length ? updatedErrors : null);
  }

  private setErrorOnCondition(control: FormControl, condition: boolean, error: string): void {
    if (condition) {
      this.addControlError(control, { [error]: true });
    } else {
      this.removeControlError(control, error);
    }
  }
}
